// MUI
import {
  Grid,
  Typography,
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";

// Project import
import YesNoCheckBoxes from "components/input/yesNoCheckboxes";
import Selector from "components/select/selector";
import { useTranslation } from "utils/locales/utilityFunctions";
import { BIOMASS_CATEGORIES, HNV, HNV_FOREST } from "./options";
import { ActivityBiomassScreening } from "types/firestoreFeatures";
import ToolbarDivider from "components/display/toolbarDivider";

type NatureSectionProps = {
  biomassScreeningData: ActivityBiomassScreening;
  inputDisabled: boolean;
  setBiomassScreeningData: ({
    value,
    key,
    parent,
  }: {
    value: any;
    key: string;
    parent?: string;
  }) => void;
  showNatureValues?: boolean;
  renderUploadButton: (mode: "light" | "dark", categories: string[]) => JSX.Element;
};

const NatureSection = ({
  biomassScreeningData,
  inputDisabled,
  setBiomassScreeningData,
  renderUploadButton,
  showNatureValues,
}: NatureSectionProps) => {
  const { translate } = useTranslation();
  const theme = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h5" sx={{ mb: -1 }}>
          {translate("biomass-project-form-nature-and-protection")}
        </Typography>
      </Grid>

      {/* Forest */}
      {biomassScreeningData?.category === BIOMASS_CATEGORIES[1] && (
        <>
          <Grid item xs={12} sm={biomassScreeningData?.natureAndConservation?.hnvForestAbove9 ? 4 : 12} >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Stack direction="row">
                <Box>
                  <YesNoCheckBoxes
                    title={translate("biomass-project-form-hnv-over-9")}
                    yesLabel={translate("biomass-project-form-yes")}
                    noLabel={translate("biomass-project-form-no")}
                    state={biomassScreeningData?.natureAndConservation?.hnvForestAbove9}
                    setState={(value) =>
                      setBiomassScreeningData({
                        value,
                        key: "hnvForestAbove9",
                        parent: "natureAndConservation",
                      })
                    }
                    disabled={inputDisabled}
                  />
                </Box>

                {biomassScreeningData?.natureAndConservation?.hnvForestAbove9 === "yes" && (
                  <Box sx={{ mt: 1, ml: 2 }}>
                    {renderUploadButton("light", ["natureAndConservation", "hnvForestAbove9"])}
                  </Box>
                )}
              </Stack>
            </Box>
          </Grid>

          {biomassScreeningData?.natureAndConservation?.hnvForestAbove9 && (
            <>
              <Grid item xs={12} sm={4}>
                <Stack direction="row">
                  {matchDownSM && (
                    <Box sx={{ mr: 1, alignSelf: "stretch" }}>
                      <ToolbarDivider orientation={"horizontal"} />
                    </Box>
                  )}

                  <Box sx={{ minWidth: 150 }}>
                    <Typography color="secondary" sx={{ mb: 1 }}>
                      {translate("biomass-project-form-hnv-forest-value")}
                    </Typography>
                    <Selector
                      title={translate("biomass-project-form-value")}
                      value={
                        biomassScreeningData?.natureAndConservation?.hnvValue
                          ? biomassScreeningData?.natureAndConservation?.hnvValue
                          : ""
                      }
                      options={HNV_FOREST}
                      label
                      fullWidth
                      changeHandler={(e) =>
                        setBiomassScreeningData({
                          value: e.target.value,
                          key: "hnvValue",
                          parent: "natureAndConservation",
                        })
                      }
                      required
                      disabled={inputDisabled}
                    />
                  </Box>
                </Stack>
              </Grid>

              {!matchDownSM && <Grid item xs={12} sm={4} />}
            </>
          )}
        </>
      )}

      {/* Non-forest */}
      {biomassScreeningData?.category !== BIOMASS_CATEGORIES[1] && (
        <>
          <Grid item xs={12} sm={biomassScreeningData?.natureAndConservation?.hnvAbove5 ? 4 : 12}>
            <YesNoCheckBoxes
              title={translate("biomass-project-form-hnv-over-5")}
              yesLabel={translate("biomass-project-form-yes")}
              noLabel={translate("biomass-project-form-no")}
              state={biomassScreeningData?.natureAndConservation?.hnvAbove5}
              setState={(value) =>
                setBiomassScreeningData({
                  value,
                  key: "hnvAbove5",
                  parent: "natureAndConservation",
                })
              }
              disabled={inputDisabled}
            />
          </Grid>

          {biomassScreeningData?.natureAndConservation?.hnvAbove5 && (
            <>
              <Grid item xs={12} sm={4}>
                <Stack direction="row">
                  {matchDownSM && (
                    <Box sx={{ mr: 1, alignSelf: "stretch" }}>
                      <ToolbarDivider orientation={"horizontal"} />
                    </Box>
                  )}

                  <Box sx={{ minWidth: 150 }}>
                    <Typography color="secondary" sx={{ mb: 1 }}>
                      {translate("biomass-project-form-hnv-value")}
                    </Typography>
                    <Selector
                      title={translate("biomass-project-form-value")}
                      value={
                        biomassScreeningData?.natureAndConservation?.hnvValue
                          ? biomassScreeningData?.natureAndConservation?.hnvValue
                          : ""
                      }
                      options={HNV}
                      label
                      fullWidth
                      changeHandler={(e) =>
                        setBiomassScreeningData({
                          value: e.target.value,
                          key: "hnvValue",
                          parent: "natureAndConservation",
                        })
                      }
                      required
                      disabled={inputDisabled}
                    />
                  </Box>
                </Stack>
              </Grid>

              {!matchDownSM && <Grid item xs={12} sm={4} />}
            </>
          )}
        </>
      )}

      {/* NBL */}
      <>
        <Grid item xs={12} sm={biomassScreeningData?.natureAndConservation?.nbl === "yes" ? 4 : 12}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Stack direction="row">
              <Box>
                <YesNoCheckBoxes
                  title={translate("biomass-project-form-nbl")}
                  yesLabel={translate("biomass-project-form-yes")}
                  noLabel={translate("biomass-project-form-no")}
                  state={biomassScreeningData?.natureAndConservation?.nbl}
                  setState={(value) => {
                    setBiomassScreeningData({
                      value,
                      key: "nbl",
                      parent: "natureAndConservation",
                    });
                  }}
                  disabled={biomassScreeningData?.riscAssessment.includes("naturecareS3") || inputDisabled}
                />
              </Box>

              {biomassScreeningData?.natureAndConservation?.nbl === "yes" && (
                <Box sx={{ mt: 1, ml: 1 }}>
                  {renderUploadButton("light", ["natureAndConservation", "nbl"])}
                </Box>
              )}
            </Stack>
          </Box>
        </Grid>

        {/* {biomassScreeningData?.natureAndConservation?.nbl === "yes" && (
          <Grid item xs={12} sm={8}>
            <Stack direction="row">
              {matchDownSM && (
                <Box sx={{ mr: 1, alignSelf: "stretch" }}>
                  <ToolbarDivider orientation={"horizontal"} />
                </Box>
              )}

              <Box>
                <YesNoCheckBoxes
                  title={translate("biomass-project-form-heathland")}
                  yesLabel={translate("biomass-project-form-yes")}
                  noLabel={translate("biomass-project-form-no")}
                  state={biomassScreeningData?.natureAndConservation?.heathland}
                  setState={(value) => {
                    setBiomassScreeningData({
                      value,
                      key: "heathland",
                      parent: "natureAndConservation",
                    });
                  }}
                  disabled={inputDisabled}
                />
              </Box>
            </Stack>
          </Grid>
        )} */}
      </>

      {/* Natura2000 */}
      <>
        <Grid
          item
          xs={12}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box>
              <YesNoCheckBoxes
                title={translate("biomass-project-form-natura2000")}
                yesLabel={translate("biomass-project-form-yes")}
                noLabel={translate("biomass-project-form-no")}
                state={biomassScreeningData?.natureAndConservation?.natura2000}
                setState={(value) =>
                  setBiomassScreeningData({
                    value,
                    key: "natura2000",
                    parent: "natureAndConservation",
                  })
                }
                disabled={inputDisabled}
              />
            </Box>
            {biomassScreeningData?.natureAndConservation?.natura2000 === "yes" && (
              <Box sx={{ mt: 1, ml: 1 }}>
                {renderUploadButton("light", ["natureAndConservation", "natura2000"])}
              </Box>
            )}
          </Box>
        </Grid>
      </>

      {/* Heritage site */}
      <Grid item xs={12}>
        <YesNoCheckBoxes
          title={translate("biomass-project-form-heritage-site")}
          yesLabel={translate("biomass-project-form-yes")}
          noLabel={translate("biomass-project-form-no")}
          state={biomassScreeningData?.natureAndConservation?.heritageSite}
          setState={(value) =>
            setBiomassScreeningData({
              value,
              key: "heritageSite",
              parent: "natureAndConservation",
            })
          }
          disabled={inputDisabled}
        />
      </Grid>

      {/* Redlist */}
      <Grid item xs={12}>
        <YesNoCheckBoxes
          title={translate("biomass-project-form-redlist-species")}
          yesLabel={translate("biomass-project-form-yes")}
          noLabel={translate("biomass-project-form-no")}
          state={biomassScreeningData?.natureAndConservation?.redlistSpecies}
          setState={(value) =>
            setBiomassScreeningData({
              value,
              key: "redlistSpecies",
              parent: "natureAndConservation",
            })
          }
          disabled={inputDisabled}
        />
      </Grid>

      {/* SKL25 */}
      <Grid item xs={12}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box>
            <YesNoCheckBoxes
              title={translate("biomass-project-form-skl25")}
              yesLabel={translate("biomass-project-form-yes")}
              noLabel={translate("biomass-project-form-no")}
              state={biomassScreeningData?.natureAndConservation?.skl25}
              setState={(value) =>
                setBiomassScreeningData({
                  value,
                  key: "skl25",
                  parent: "natureAndConservation",
                })
              }
              disabled={inputDisabled}
            />
          </Box>
          {/* {biomassScreeningData?.natureAndConservation?.skl25 === "yes" && (
            <Box sx={{ mt: 1, ml: 2 }}>{renderUploadButton("light", ["natureAndConservation", "skl25"])}</Box>
          )} */}
        </Box>
      </Grid>

      {/* Nature values */}
      {showNatureValues && (
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box>
              <Typography color="secondary">
                {translate("biomass-project-form-nature-values")}
              </Typography>

              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={biomassScreeningData?.natureAndConservation?.natureValues === "field"}
                      onChange={() =>
                        setBiomassScreeningData({
                          value:
                            biomassScreeningData?.natureAndConservation?.natureValues === "field"
                              ? null
                              : "field",
                          key: "natureValues",
                          parent: "natureAndConservation",
                        })
                      }
                      disabled={inputDisabled}
                    />
                  }
                  label={translate("biomass-project-form-nature-values-field")}
                  sx={{ ml: 0, mb: -1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={biomassScreeningData?.natureAndConservation?.natureValues === "map"}
                      onChange={() => {
                        setBiomassScreeningData({
                          value:
                            biomassScreeningData?.natureAndConservation?.natureValues === "map"
                              ? null
                              : "map",
                          key: "natureValues",
                          parent: "natureAndConservation",
                        });
                      }}
                      disabled={inputDisabled}
                    />
                  }
                  label={translate("biomass-project-form-nature-values-map")}
                  sx={{ mb: -1 }}
                />
              </FormGroup>
            </Box>
            {biomassScreeningData?.natureAndConservation?.natureValues === "field" && (
              <Box sx={{ mt: 1, ml: 0 }}>{renderUploadButton("light", ["natureAndConservation", "natureValues"])}</Box>
            )}
          </Box>
        </Grid>
      )}
    </>
  );
};

export default NatureSection;
